import { IonImg } from '@ionic/react';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import React from 'react';
import type { ChallengeStyleData } from 'services/casino';
import { getCasinoFolderName, getCasinoFolderName as getCasinoFolderNameCommon, S3_CASINOS_BASE } from 'common/common';

export type EventCardVariant = 'feature' | 'list';

export type { ChallengeStyleData };
export { getCasinoFolderNameCommon as getCasinoFolderName };

const EVENTS_BANNER_S3_BASE = S3_CASINOS_BASE;

const formatStartDate = (date: Date): string => {
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11 ? 'st'
      : day % 10 === 2 && day !== 12 ? 'nd'
        : day % 10 === 3 && day !== 13 ? 'rd'
          : 'th';
  return `Starting ${month} ${day}${suffix}`;
};

/**
 * CTA label priority:
 * 1. Event completed (now > endDate)        → "Results"
 * 2. Event not started (now < startDate)    → "Starting [Month Day]"
 * 3. Running + enrolled                     → "Play Now"
 * 4. Running + pending                      → "Join Now"
 * 5. Default                               → ctaLabel (e.g. "Learn More")
 */
export const getCtaLabel = (
  participantStatus?: string,
  startDate?: string | Date,
  endDate?: string | Date,
  defaultCtaLabel?: string,
  active?: boolean
): string | undefined => {
  const normalizedStatus = participantStatus?.toString().trim().toLowerCase();
  const now = new Date();

  if ((endDate && now > new Date(endDate)) || active === false) {
    return 'Results';
  }

  // Not started yet
  if (startDate && now < new Date(startDate)) {
    return formatStartDate(new Date(startDate));
  }

  if (normalizedStatus === 'enrolled') {
    return 'Leaderboard';
  }

  // Running: started (or no startDate) and not ended (or no endDate) and active !== false
  const isRunning =
    (!startDate || now >= new Date(startDate)) && (!endDate || now <= new Date(endDate));
  if (isRunning && normalizedStatus !== 'enrolled') {
    return 'Play Now!';
  }

  return defaultCtaLabel;
};


const isAbsoluteUrl = (value?: string) => {
  const normalized = (value || '').toLowerCase();
  return (
    normalized.startsWith('http://') ||
    normalized.startsWith('https://') ||
    normalized.startsWith('//')
  );
};

export const buildCommonUrl = (subPath: string, path?: string): string | undefined => {
  if (!path) return undefined;
  if (isAbsoluteUrl(path)) return path;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${EVENTS_BANNER_S3_BASE}/${subPath}${normalizedPath}`;
};

export const buildChallengeAssetUrl = (path?: string) =>
  buildCommonUrl('assets/challenges', path);

export const buildRewardAssetUrl = (path?: string) =>
  buildCommonUrl('assets/rewards', path);

/**
 * The per-challenge "complete" badge lives alongside the challenge's other
 * assets, one folder up from the background image: `/<challengeFolder>/complete.png`.
 */
export const getCompleteImagePath = (background?: string): string => {
  if (!background) return 'complete.png';
  const normalized = background.startsWith('/') ? background.slice(1) : background;
  const segments = normalized.split('/').filter(Boolean);
  return segments.length > 0 ? `/${segments[0]}/complete.png` : 'complete.png';
};

const isChallengeCompleted = (endDate?: string | Date, viewableDate?: string | Date): boolean => {
  if (!endDate || !viewableDate) return false;
  const now = new Date();
  return now > new Date(endDate) && now < new Date(viewableDate);
};

/**
 * Resolves the URL of the challenge's "complete" overlay image, or undefined when
 * the challenge isn't completed. Shared by the list card (FeatureEventCard) and the
 * detail card (DetailEventCard) so both surface the badge identically.
 */
export const resolveCompleteImageUrl = (
  styleData?: ChallengeStyleData,
  endDate?: string | Date,
  viewableDate?: string | Date
): string | undefined => {
  const withinViewableWindow = isChallengeCompleted(endDate, viewableDate);
  const fullyCompleted = viewableDate ? new Date() > new Date(viewableDate) : false;
  return withinViewableWindow || fullyCompleted
    ? buildChallengeAssetUrl(getCompleteImagePath(styleData?.background))
    : undefined;
};

export const resolveDescription = (
  description?: string,
  descriptions?: { pending?: string; enrolled?: string; running?: string; complete?: string },
  participantStatus?: string,
  endDate?: string | Date,
  justJoined?: boolean
): string | undefined => {
  if (!descriptions) return description;
  const now = new Date();
  if (endDate && now > new Date(endDate)) return descriptions.complete ?? description;
  if (justJoined) return descriptions.enrolled ?? description;
  const status = participantStatus?.toLowerCase();
  if (status === 'enrolled') return descriptions.running ?? description;
  return descriptions.pending ?? description;
};

/**
 * Descriptions arrive from the API as raw strings that may contain inline
 * formatting tags (e.g. <strong>, <b>). Rendering them as plain text shows the
 * tags literally, so when the subtitle is a string we sanitize it (allowing only
 * a small set of formatting tags, stripping anything dangerous) and render it as
 * HTML. Non-string ReactNodes are rendered as-is.
 */
const SUBTITLE_SANITIZE_OPTIONS = {
  ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'br', 'span'],
  ALLOWED_ATTR: [] as string[],
};

export const EventCardSubtitle: React.FC<{
  subtitle?: React.ReactNode;
  color?: string;
}> = ({ subtitle, color }) => {
  if (!subtitle) return null;

  if (typeof subtitle === 'string') {
    return (
      <p
        className="event-card__subtitle"
        style={{ color }}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(subtitle, SUBTITLE_SANITIZE_OPTIONS),
        }}
      />
    );
  }

  return (
    <p className="event-card__subtitle" style={{ color }}>
      {subtitle}
    </p>
  );
};

interface EventCardProps {
  title: React.ReactNode;
  variant?: EventCardVariant;
  subtitle?: React.ReactNode;
  ctaLabel?: string;
  onCtaClick?: () => void;
  onExploreClick?: () => void;
  onCardClick?: () => void;
  imageSrc?: string;
  imageAlt?: string;
  showBadge?: boolean;
  backslash?: boolean;
  className?: string;
  joined?: boolean;
  casinoLogoSrc?: string;
  styleData?: ChallengeStyleData;
  participantStatus?: string;
  startDate?: string | Date;
  endDate?: string | Date;
  active?: boolean;
  descriptions?: { pending?: string; enrolled?: string; running?: string; complete?: string };
  justJoined?: boolean;
}

export const FeatureEventCard: React.FC<EventCardProps> = ({
  title,
  subtitle,
  ctaLabel,
  onCtaClick,
  onExploreClick,
  onCardClick,
  imageSrc,
  imageAlt = 'Event',
  backslash = true,
  className,
  joined = false,
  casinoLogoSrc,
  styleData,
  participantStatus,
  startDate,
  endDate,
  active,
  descriptions,
  justJoined,
}) => {
  const resolvedSubtitle = resolveDescription(
    typeof subtitle === 'string' ? subtitle : undefined,
    descriptions,
    participantStatus,
    endDate,
    justJoined
  ) ?? subtitle;

  const derivedCharImage = buildChallengeAssetUrl(styleData?.charimg);
  const derivedBackgroundImage = buildChallengeAssetUrl(styleData?.background);
  const resolvedImageSrc = derivedCharImage || imageSrc;

  // Mirror the detail card: once the challenge has ended, show the per-challenge
  // "complete" badge in the lower-right corner (in place of the Explore button).
  const completeImageUrl = resolveCompleteImageUrl(styleData, endDate, endDate);

  const resolvedBackgroundImage = derivedBackgroundImage;
  const cardStyle: React.CSSProperties | undefined =
    styleData?.backgroundColor || styleData?.primaryColor || resolvedBackgroundImage
      ? {
        backgroundColor: styleData?.backgroundColor || styleData?.primaryColor || undefined,
        ...(resolvedBackgroundImage
          ? { backgroundImage: `url(${resolvedBackgroundImage})` }
          : {}),
      }
      : undefined;

  return (
    <div
      className={clsx(
        'event-card',
        'event-card--feature',
        backslash ? 'event-card--backslash' : null,
        className
      )}
      style={cardStyle}
      onClick={onCardClick}
    >
      <div className="event-card__content" style={styleData?.alignTop ? { alignSelf: 'stretch', paddingBottom: '14px' } : undefined}>
        <h2 className="event-card__title" style={{ color: styleData?.textColor }}>
          {title}
        </h2>
        <EventCardSubtitle subtitle={resolvedSubtitle} color={styleData?.secondaryColor} />
        {(() => {
          const effectiveCtaLabel = getCtaLabel(joined ? 'enrolled' : 'pending', startDate, endDate, ctaLabel, active);
          if (effectiveCtaLabel) {
            return (
              <button
                className="event-card__cta"
                onClick={(e) => {
                  e.stopPropagation();
                  onCtaClick && onCtaClick();
                }}
                style={{
                  background: styleData?.btnJoinBackground || styleData?.ctaBackground || styleData?.btncolor || undefined,
                  color: styleData?.btnJoinColor || styleData?.textColor,
                  ...(styleData?.alignTop ? { marginTop: 'auto' } : {}),
                }}
              >
                {effectiveCtaLabel}
              </button>
            );
          }
          if (joined) {
            return (
              <div className="event-card__joined" style={styleData?.alignTop ? { marginTop: 'auto' } : undefined}>
                {casinoLogoSrc ? (
                  <img src={casinoLogoSrc} alt="Casino" style={{ height: 36, marginRight: 12 }} />
                ) : null}
                {casinoLogoSrc ? (
                  <span aria-hidden="true" className="event-card__joined-divider" />
                ) : null}
                <img
                  src="assets/images/SlotCheck-White.png"
                  alt="SlotCheck"
                  style={{ width: 58, height: 40, objectFit: 'contain' }}
                />
              </div>
            );
          }
          return null;
        })()}


      </div>
      {!completeImageUrl && onExploreClick && (
        <button
          className="event-card__cta event-card__ctaexplore"
          onClick={(e) => {
            e.stopPropagation();
            onExploreClick();
          }}
          style={{
            background: styleData?.ctaBackground || 'rgba(0, 0, 0, 0.25)',
            color: styleData?.textColor,
          }}
        >
          <svg
            className="event-card__explore-icon"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12.1959 3.99994C16.7194 3.99994 20.3918 7.67239 20.3918 12.1959C20.3918 16.7194 16.7194 20.3918 12.1959 20.3918C7.67239 20.3918 3.99994 16.7194 3.99994 12.1959C3.99994 7.67239 7.67239 3.99994 12.1959 3.99994ZM12.623 19.5254C16.3352 19.3121 19.3121 16.3352 19.5254 12.623H18.3713C18.1356 12.623 17.9442 12.4316 17.9442 12.1959C17.9442 11.9602 18.1356 11.7688 18.3713 11.7688H19.5254C19.3121 8.05652 16.3352 5.07967 12.623 4.86631V6.02046C12.623 6.25617 12.4316 6.44756 12.1959 6.44756C11.9602 6.44756 11.7688 6.25617 11.7688 6.02046V4.86631C8.05652 5.07967 5.07967 8.05652 4.86631 11.7688H6.02046C6.25617 11.7688 6.44756 11.9602 6.44756 12.1959C6.44756 12.4316 6.25617 12.623 6.02046 12.623H4.86631C5.07967 16.3352 8.05652 19.3121 11.7688 19.5254V18.3713C11.7688 18.1356 11.9602 17.9442 12.1959 17.9442C12.4316 17.9442 12.623 18.1356 12.623 18.3713V19.5254Z"
              fill="white"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14.7595 15.0703L8.19949 16.7103C8.05398 16.7466 7.90002 16.704 7.79391 16.5979C7.68785 16.4918 7.64521 16.3379 7.68157 16.1924L9.32157 9.63235C9.32391 9.62305 9.32654 9.61386 9.32949 9.60476L9.3324 9.59599C9.35729 9.52424 9.4005 9.46139 9.45655 9.41288C9.50718 9.36922 9.56749 9.33789 9.63235 9.32157L16.1924 7.68157C16.3379 7.64521 16.4918 7.68785 16.5979 7.79391C16.704 7.90002 16.7466 8.05398 16.7103 8.19949L15.0702 14.7595C15.056 14.8163 15.0301 14.8698 14.9945 14.9163C14.9474 14.9775 14.8841 15.0254 14.8108 15.0539L14.8027 15.057C14.7886 15.0621 14.7742 15.0666 14.7595 15.0703ZM10.0363 10.2954L8.68288 15.709L14.0809 14.3595C13.2511 12.385 11.9103 11.0218 10.0363 10.2954Z"
              fill="white"
            />
          </svg>
          Explore
        </button>
      )}
      {resolvedImageSrc && (
        <div className="event-card__image">
          <IonImg src={resolvedImageSrc} alt={imageAlt} />
        </div>
      )}
      {completeImageUrl && (
        <div
          className="event-card__complete-overlay"
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            zIndex: 999,
            cursor: onExploreClick ? 'pointer' : undefined,
          }}
          role={onExploreClick ? 'button' : undefined}
          tabIndex={onExploreClick ? 0 : undefined}
          onClick={
            onExploreClick
              ? (e) => {
                e.stopPropagation();
                onExploreClick();
              }
              : undefined
          }
          onKeyDown={
            onExploreClick
              ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onExploreClick();
                }
              }
              : undefined
          }
        >
          <IonImg src={completeImageUrl} alt="Completed" />
        </div>
      )}
    </div>
  );
};

export const ListEventCard: React.FC<EventCardProps> = ({
  title,
  showBadge = true,
  onCardClick,
  backslash = true,
  className,
}) => {
  return (
    <div
      className={clsx(
        'event-card',
        'event-card--list',
        backslash ? 'event-card--backslash' : null,
        className
      )}
      onClick={onCardClick}
    >
      <div className="event-card__list-title">{title}</div>
      {showBadge && <div className="event-card__list-badge" aria-hidden="true" />}
    </div>
  );
};

const EventCard: React.FC<EventCardProps> = ({ variant = 'feature', ...props }) => {
  return variant === 'list' ? (
    <ListEventCard variant={variant} {...props} />
  ) : (
    <FeatureEventCard variant={variant} {...props} />
  );
};

export default EventCard;
